import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

export type RichTableProps = {
  /**
   * One flex grow ratio per column; its length is the column count.
   *
   * Ratios rather than percentages, so inserting or removing a column never
   * has to renormalize the others — the same choice `affine:column.width`
   * makes.
   */
  columnWidths: number[];
  /** Style the first row as a header. */
  headerRow: boolean;
};

export type RichTableRowProps = Record<string, never>;

export type RichTableCellProps = Record<string, never>;

/**
 * A table whose cells hold real blocks rather than flat `Text`.
 *
 * `affine:table` stores cells as `Record<'row:col', { text: Text }>` with
 * `isFlatData` and `children: []`, which is why nothing block-level — markdown
 * shortcuts, the slash menu, the drag handle — can work inside one. Here every
 * cell is an ordinary block container, so all of that works with no extra
 * wiring.
 */
export const RichTableBlockSchema = defineBlockSchema({
  flavour: 'affine:rich-table',
  props: (): RichTableProps => ({
    columnWidths: [],
    headerRow: true,
  }),
  metadata: {
    version: 1,
    role: 'hub',
    parent: ['affine:note', '@hub'],
    children: ['affine:rich-table-row'],
  },
  toModel: () => new RichTableBlockModel(),
});

export const RichTableRowBlockSchema = defineBlockSchema({
  flavour: 'affine:rich-table-row',
  props: (): RichTableRowProps => ({}),
  metadata: {
    version: 1,
    role: 'hub',
    parent: ['affine:rich-table'],
    children: ['affine:rich-table-cell'],
  },
  toModel: () => new RichTableRowBlockModel(),
});

export const RichTableCellBlockSchema = defineBlockSchema({
  flavour: 'affine:rich-table-cell',
  props: (): RichTableCellProps => ({}),
  metadata: {
    version: 1,
    role: 'hub',
    parent: ['affine:rich-table-row'],
    children: [
      '@content',
      'affine:columns',
      'affine:database',
      'affine:data-view',
      'affine:callout',
    ],
  },
  toModel: () => new RichTableCellBlockModel(),
});

export class RichTableBlockModel extends BlockModel<RichTableProps> {}
export class RichTableRowBlockModel extends BlockModel<RichTableRowProps> {}
export class RichTableCellBlockModel extends BlockModel<RichTableCellProps> {}

export const RichTableBlockSchemaExtension =
  BlockSchemaExtension(RichTableBlockSchema);
export const RichTableRowBlockSchemaExtension = BlockSchemaExtension(
  RichTableRowBlockSchema
);
export const RichTableCellBlockSchemaExtension = BlockSchemaExtension(
  RichTableCellBlockSchema
);
